import { useEffect, useRef, useState } from "react";
import "./globalCapDetailProcess.css";

export default function GlobalCapDetailProcess({ data }) {
  const items = data?.items ?? [];
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      setActive(items.length - 1);
      return undefined;
    }

    const reveal = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          reveal.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    reveal.observe(node);

    const track = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(Number(entry.target.dataset.index));
          }
        });
      },
      { rootMargin: "-42% 0px -48% 0px", threshold: 0 }
    );
    stepRefs.current.forEach((step) => step && track.observe(step));

    const lastStep = stepRefs.current[items.length - 1];
    const finish = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActive(items.length - 1);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 1 }
    );
    if (lastStep) finish.observe(lastStep);

    return () => {
      reveal.disconnect();
      track.disconnect();
      finish.disconnect();
    };
  }, [items.length]);

  if (!items.length) return null;

  const current = Math.min(active, items.length - 1);
  const progress = ((current + 1) / items.length) * 100;

  return (
    <section
      ref={sectionRef}
      className={`gcdp ${visible ? "gcdp--visible" : ""}`}
    >
      <div className="gcdp__grid" aria-hidden="true" />

      <div className="gcdp__container">
        <aside className="gcdp__aside">
          <div className="gcdp__aside-inner">
            {data.eyebrow && (
              <span className="gcdp__eyebrow">
                <span className="gcdp__eyebrow-dot" aria-hidden="true" />
                {data.eyebrow}
              </span>
            )}

            <h2 className="gcdp__title">{data.title}</h2>

            {data.intro && <p className="gcdp__intro">{data.intro}</p>}

            <div className="gcdp__meter" aria-hidden="true">
              <span
                className="gcdp__meter-fill"
                style={{ transform: `scaleX(${progress / 100})` }}
              />
            </div>

            <p className="gcdp__now" aria-hidden="true">
              <span key={current}>{items[current].title}</span>
            </p>
          </div>
        </aside>

        <ol className="gcdp__rail">
          {items.map((item, index) => {
            const reached = index <= current;
            const isCurrent = index === current;
            const isLast = index === items.length - 1;

            return (
              <li
                key={item.title}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-index={index}
                className={`gcdp__step ${reached ? "gcdp__step--reached" : ""} ${
                  isCurrent ? "gcdp__step--current" : ""
                } ${index < current ? "gcdp__step--passed" : ""}`}
                style={{ "--i": index }}
              >
                <div className="gcdp__marker" aria-hidden="true">
                  <span className="gcdp__node" />
                  {!isLast && <span className="gcdp__link" />}
                </div>

                <article className="gcdp__card">
                  <span className="gcdp__step-label">
                    {item.step ?? `Step ${index + 1}`}
                  </span>
                  <h3 className="gcdp__step-title">{item.title}</h3>
                  <p className="gcdp__step-text">{item.description}</p>
                </article>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
